import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import {
  CREDIT_NOTE_TABS,
  creditNoteActionPermission,
  creditNoteListColumnPermission,
  creditNoteListControlPermission,
  creditNotePermission,
} from "./creditNotePermissions";

const tabColors = {
  total: { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  current_month: { color: "#2563eb", background: "#dbeafe", border: "#93c5fd" },
  paid: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
  cancelled: { color: "#ef4444", background: "#fee2e2", border: "#fecaca" },
};

const headerCellSx = {
  backgroundColor: "#f8f9fa", color: "#64748b", fontSize: "10px", fontWeight: 600,
  py: 0.9, px: 0.45, lineHeight: 1.15, whiteSpace: "normal", borderBottom: "1px solid #e9ecef",
};
const bodyCellSx = {
  color: "#334155", fontSize: "10px", py: 0.7, px: 0.45, lineHeight: 1.4,
  verticalAlign: "top", borderBottom: "1px solid #edf2f7", whiteSpace: "normal",
  overflowWrap: "anywhere", "& .MuiTypography-root": { fontSize: "inherit", lineHeight: "inherit" },
};
const columnWidths = {
  selection: 28, serial: 34, credit_note_number: "12%", source_invoice_number: "12%",
  billing_details: "16%", actions: 64,
};
const columnCellSx = (key) => ({
  width: columnWidths[key], minWidth: 0,
  ...(key === "selection" ? { padding: "0 !important" } : {}),
});
const emptyMeta = { current_page: 1, last_page: 1, per_page: 50, total: 0, from: 0, to: 0 };
const emptyCounts = { total: 0, current_month: 0, paid: 0, cancelled: 0 };

const statusOptions = [
  { value: "approved", label: "Approved" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rejected", label: "Rejected" },
];
const paymentOptions = [
  { value: "pending", label: "Payment Pending" },
  { value: "paid", label: "Paid" },
];
const emailOptions = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
];
const bulkActions = [
  { value: "status", label: "Change Status", permission: "bulk_update_status" },
  { value: "payment_status", label: "Change Payment Status", permission: "bulk_update_payment_status" },
  { value: "email_status", label: "Change Email Status", permission: "bulk_update_email_status" },
];
const bulkValues = { status: statusOptions, payment_status: paymentOptions, email_status: emailOptions };
const formatNumber = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const responseMessage = (result, fallback) => (
  Object.values(result?.errors || {}).flat().find(Boolean) || result?.message || fallback
);

function CreditNoteList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission } = useModulePermissions(permissions, userPermission, "credit_notes");
  const has = useCallback((permission) => hasModulePermission(permission), [hasModulePermission]);

  const visibleTabs = useMemo(
    () => CREDIT_NOTE_TABS.filter((item) => has(creditNotePermission("tabs", item.value))),
    [has]
  );
  const [tab, setTab] = useState(visibleTabs[0]?.value || "total");
  const [filters, setFilters] = useState({
    start_date: "", end_date: "", status: null, payment_status: null,
    credit_note_month: null, email_status: null, client: null, search: "",
  });
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [creditNoteMonths, setCreditNoteMonths] = useState([]);
  const [counts, setCounts] = useState(emptyCounts);
  const [meta, setMeta] = useState(emptyMeta);
  const [exchangeAmount, setExchangeAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkValue, setBulkValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ open: false, color: "success", message: "" });

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  }), []);

  const notify = (message, color = "success") => setNotice({ open: true, color, message });
  const setFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  };

  const queryString = useMemo(() => {
    const query = new URLSearchParams({ tab, page: String(page), per_page: "50" });
    Object.entries(filters).forEach(([key, value]) => {
      const normalized = value && typeof value === "object" ? value.value : value;
      if (normalized !== "" && normalized !== null && normalized !== undefined) {
        query.set(key === "client" ? "client_id" : key, String(normalized));
      }
    });
    return query.toString();
  }, [filters, page, tab]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/credit-notes?${queryString}`, { headers });
      const result = await response.json();
      if (!response.ok) throw new Error(responseMessage(result, "Unable to load credit notes."));
      setRows(result.data || []);
      setClients((result.clients || []).map((client) => ({ value: client.id, label: client.name })));
      setCreditNoteMonths(result.credit_note_months || []);
      setCounts({ ...emptyCounts, ...(result.counts || {}) });
      setMeta({ ...emptyMeta, ...(result.meta || {}) });
      setExchangeAmount(Number(result.exchange_amount_usd || 0));
      setSelectedIds([]);
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [headers, queryString]);

  useEffect(() => {
    const timer = window.setTimeout(loadRows, 250);
    return () => window.clearTimeout(timer);
  }, [loadRows]);

  useEffect(() => {
    if (location.state?.message) {
      notify(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!visibleTabs.some((item) => item.value === tab) && visibleTabs[0]) setTab(visibleTabs[0].value);
  }, [tab, visibleTabs]);

  const download = async (path, fallbackName) => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}${path}`, { headers });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(responseMessage(result, "Export failed."));
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = match ? decodeURIComponent(match[1]) : fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const applyBulk = async () => {
    if (!selectedIds.length || !bulkAction?.value || !bulkValue?.value) {
      notify("Select credit notes, an action and a value first.", "warning");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/credit-notes/bulk-workflow-status`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedIds, [bulkAction.value]: bulkValue.value }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(responseMessage(result, "Bulk update failed."));
      notify(result.message || "Selected credit notes updated.");
      setBulkAction(null);
      setBulkValue(null);
      loadRows();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const visibleColumns = useMemo(() => [
    ["selection", ""], ["serial", "#"], ["credit_note_number", "Credit Note Number"],
    ["source_invoice_number", "Invoice Number"], ["billing_details", "Billing Details"],
    ["credit_note_date", "Credit Note Date"], ["usd_amount", "USD Amount"],
    ["final_amount", "Exchange Amount"], ["payment_status", "Payment Status"],
    ["email_status", "Email Status"], ["status", "Status"], ["actions", "Actions"],
  ].filter(([key]) => has(creditNoteListColumnPermission(key))), [has]);

  const allowedBulkActions = useMemo(
    () => bulkActions.filter((item) => has(creditNoteActionPermission(item.permission))),
    [has]
  );
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : rows.map((row) => row.id));
  const toggleOne = (id) => setSelectedIds((current) => (
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
  ));

  const renderCell = (key, row, index) => {
    if (key === "selection") return <Checkbox size="small" checked={selectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} />;
    if (key === "serial") return (meta.from || 1) + index;
    if (key === "credit_note_number") return row.credit_note_number;
    if (key === "source_invoice_number") return (row.source_invoice_numbers || []).join(", ") || "-";
    if (key === "billing_details") return <SoftTypography variant="caption" fontWeight="bold">{row.billing_details || "-"}</SoftTypography>;
    if (key === "credit_note_date") return row.credit_note_date || "-";
    if (key === "usd_amount") return `$${formatNumber(row.source_amount_usd)} USD`;
    if (key === "final_amount") return `${row.currency_symbol || row.currency_code || ""}${formatNumber(row.total_amount)} ${row.currency_code || ""}`;
    if (key === "payment_status") {
      const color = row.payment_status === "paid" ? "#65d600" : "#f4b400";
      return <span style={{ color, fontWeight: 600 }}>{row.payment_status === "paid" ? "Paid" : "Pending"}</span>;
    }
    if (key === "email_status") return `${row.email_status === "sent" ? "Sent" : "Pending"}${row.email_send_count ? ` (${row.email_send_count})` : ""}`;
    if (key === "status") return String(row.status || "-").replace(/_/g, " ");
    if (key === "actions") return (
      <SoftBox display="flex" justifyContent="flex-end" sx={{ "& .MuiIconButton-root": { p: 0.5 }, "& .MuiIcon-root": { fontSize: "18px !important" } }}>
        {has(creditNotePermission("pages", "view")) && has(creditNoteActionPermission("view")) && (
          <Tooltip title="View Credit Note"><IconButton size="small" onClick={() => navigate(`/credit-notes/${row.id}/view`)}><Icon>visibility</Icon></IconButton></Tooltip>
        )}
        {has(creditNotePermission("pages", "edit")) && has(creditNoteActionPermission("edit")) && (
          <Tooltip title="Edit Credit Note"><IconButton size="small" onClick={() => navigate(`/credit-notes/${row.id}/edit`)}><Icon>edit</Icon></IconButton></Tooltip>
        )}
      </SoftBox>
    );
    return "-";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={2}>
        <SoftTypography variant="h5" mb={1.25}>Credit Notes</SoftTypography>

        <SoftBox display="grid" sx={{ gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${Math.max(visibleTabs.length, 1)}, minmax(0, 1fr))` }, gap: 1, mb: 1.5 }}>
          {visibleTabs.map((item) => {
            const palette = tabColors[item.value];
            const active = tab === item.value;
            return (
              <SoftButton key={item.value} variant="outlined" onClick={() => { setTab(item.value); setPage(1); }}
                sx={{ color: palette.color, borderColor: palette.border, backgroundColor: active ? palette.background : "#fff", textTransform: "none" }}>
                {item.label} ({counts[item.value] || 0})
              </SoftButton>
            );
          })}
        </SoftBox>

        <Card sx={{ p: 1.25, mb: 1.5 }}>
          <SoftBox display="grid" sx={(theme) => ({
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 1, alignItems: "end",
            [theme.breakpoints.down("lg")]: { "& > *": { gridColumn: "span 6" } },
            [theme.breakpoints.down("sm")]: { "& > *": { gridColumn: "span 12" } },
          })}>
            {has(creditNotePermission("filters", "start_date")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">Start Date (Created)</SoftTypography><SoftInput type="date" size="small" value={filters.start_date} onChange={(e) => setFilter("start_date", e.target.value)} /></SoftBox>}
            {has(creditNotePermission("filters", "end_date")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">End Date (Created)</SoftTypography><SoftInput type="date" size="small" value={filters.end_date} onChange={(e) => setFilter("end_date", e.target.value)} /></SoftBox>}
            {has(creditNotePermission("filters", "status")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">Status</SoftTypography><SoftSelect size="small" isClearable options={statusOptions} value={filters.status} onChange={(value) => setFilter("status", value)} /></SoftBox>}
            {has(creditNotePermission("filters", "payment_status")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">Payment Status</SoftTypography><SoftSelect size="small" isClearable options={paymentOptions} value={filters.payment_status} onChange={(value) => setFilter("payment_status", value)} /></SoftBox>}
            {has(creditNotePermission("filters", "credit_note_month")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">Credit Note Year Month</SoftTypography><SoftSelect size="small" isClearable placeholder="Year - Month" value={filters.credit_note_month} options={creditNoteMonths.map((month) => ({ value: month, label: month }))} onChange={(value) => setFilter("credit_note_month", value || null)} /></SoftBox>}
            {has(creditNotePermission("filters", "email_status")) && <SoftBox sx={{ gridColumn: "span 2" }}><SoftTypography variant="caption">Email Status</SoftTypography><SoftSelect size="small" isClearable options={emailOptions} value={filters.email_status} onChange={(value) => setFilter("email_status", value)} /></SoftBox>}
            {has(creditNotePermission("filters", "client")) && <SoftBox sx={{ gridColumn: "span 3" }}><SoftTypography variant="caption">Client</SoftTypography><SoftSelect size="small" isClearable options={clients} value={filters.client} onChange={(value) => setFilter("client", value)} /></SoftBox>}
            {has(creditNotePermission("filters", "search")) && <SoftBox sx={{ gridColumn: "span 3" }}><SoftTypography variant="caption">Search</SoftTypography><SoftInput size="small" placeholder="Credit note, invoice, billing..." value={filters.search} onChange={(e) => setFilter("search", e.target.value)} /></SoftBox>}
            {has(creditNoteActionPermission("export")) && <SoftButton disabled={busy} color="info" sx={{ gridColumn: "span 2" }} onClick={() => download(`/credit-notes/export?${queryString}`, "credit-notes.xlsx")}><Icon>download</Icon>&nbsp; Export Excel</SoftButton>}
            {has(creditNoteActionPermission("export_pdf")) && <SoftButton disabled={busy} color="error" sx={{ gridColumn: "span 2" }} onClick={() => download(`/credit-notes/export/pdf?${selectedIds.length ? `ids=${selectedIds.join(",")}` : queryString}`, "credit-notes.zip")}><Icon>picture_as_pdf</Icon>&nbsp; Export PDF</SoftButton>}
            {has(creditNotePermission("pages", "create")) && has(creditNoteActionPermission("create")) && <SoftButton color="info" sx={{ gridColumn: "span 2" }} onClick={() => navigate("/credit-notes/create")}><Icon>add</Icon>&nbsp; Generate Credit Note</SoftButton>}
          </SoftBox>
          <SoftBox display="flex" flexWrap="wrap" gap={1} justifyContent="space-between" mt={1}>
            {has(creditNoteListControlPermission("showing_entries")) && <SoftTypography variant="caption">Showing {meta.from || 0} to {meta.to || 0} of {meta.total || 0} entries</SoftTypography>}
            {has(creditNoteListControlPermission("exchange_amount")) && <SoftTypography variant="button" color="info">Exchange Amount: ${formatNumber(exchangeAmount)} USD</SoftTypography>}
          </SoftBox>
        </Card>

        {has(creditNoteListControlPermission("bulk_selection")) && allowedBulkActions.length > 0 && (
          <Card sx={{ p: 1.25, mb: 1.5 }}>
            <SoftBox display="grid" sx={{ gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "160px minmax(0, 1fr) minmax(0, 1fr) 140px" }, gap: 1, alignItems: "center" }}>
              <SoftTypography variant="button">{selectedIds.length} selected</SoftTypography>
              <SoftSelect placeholder="Select Action" options={allowedBulkActions} value={bulkAction} onChange={(value) => { setBulkAction(value); setBulkValue(null); }} />
              <SoftSelect placeholder="Select Value" isDisabled={!bulkAction} options={bulkAction ? bulkValues[bulkAction.value] : []} value={bulkValue} onChange={setBulkValue} />
              <SoftButton color="info" disabled={busy} onClick={applyBulk}>Apply</SoftButton>
            </SoftBox>
          </Card>
        )}

        <Card>
          <TableContainer sx={{ overflowX: "hidden", width: "100%", boxShadow: "none" }}>
            <Table size="small" sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}>
              <TableHead sx={{ display: "table-header-group", p: 0 }}>
                <TableRow>
                  {visibleColumns.map(([key, label]) => <TableCell key={key} sx={{ ...headerCellSx, ...columnCellSx(key), overflowWrap: "anywhere" }}>{key === "selection" ? <Checkbox size="small" checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected} onChange={toggleAll} /> : label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>No credit notes found.</TableCell></TableRow>}
                {!loading && rows.map((row, index) => <TableRow key={row.id}>{visibleColumns.map(([key]) => <TableCell key={key} sx={{ ...bodyCellSx, ...columnCellSx(key) }}>{renderCell(key, row, index)}</TableCell>)}</TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {has(creditNoteListControlPermission("pagination")) && meta.last_page > 1 && (
          <SoftBox display="flex" justifyContent="center" alignItems="center" gap={1} mt={1.5}>
            <SoftButton size="small" variant="outlined" color="info" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</SoftButton>
            <SoftTypography variant="caption">Page {meta.current_page} of {meta.last_page}</SoftTypography>
            <SoftButton size="small" variant="outlined" color="info" disabled={page >= meta.last_page} onClick={() => setPage((value) => Math.min(meta.last_page, value + 1))}>Next</SoftButton>
          </SoftBox>
        )}
      </SoftBox>
      <Footer />
      <SoftSnackbar color={notice.color} icon="notifications" title="Credit Notes" content={notice.message} open={notice.open} close={() => setNotice((current) => ({ ...current, open: false }))} />
    </DashboardLayout>
  );
}

export default CreditNoteList;
